import { useMemo } from 'react'

import { Link } from 'react-router-dom'

import { generateChatPath } from '@bettermode/common/utils'
import { Chat, QueryDirection } from '@tribeplatform/gql-client/types'
import { useChat, useMessages } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../../../i18n/index.js'
import { ChatMessage } from '../ChatMessage/ChatMessage.js'

const MESSAGES_LIMIT = 10

interface Props {
  chatId: Chat['_id']
}

export const ExistingChatPreview = ({ chatId }: Props) => {
  const { data: chat } = useChat({
    variables: {
      id: chatId,
    },
  })

  const { data: messages } = useMessages({
    variables: {
      chatId,
      direction: QueryDirection.Before,
      limit: MESSAGES_LIMIT,
      inclusive: true,
      messageId: chat?.lastMessage?.messageId,
      pageId: chat?.lastMessage?.pageId,
      addressSignature: chat?.lastMessage?.addressSignature,
    },
    useInfiniteQueryOptions: {
      enabled: !!chat,
    },
  })
  const messageItems = useMemo(() => {
    return messages?.pages.flatMap(page => page.data).reverse()
  }, [messages])

  return (
    <div className="relative flex flex-col h-full animate-fade-in rounded-base w-full justify-end">
      <div className="flex flex-col-reverse h-full gap-5 relative animate-fade-in scale-75">
        {messageItems?.map(message => (
          <ChatMessage
            key={message._id}
            id={message._id}
            chat={chat}
            message={message}
            isLastMessage={false}
            isLestReadMessage={false}
            layout="compact"
          />
        ))}
      </div>
      <div className="absolute top-0 inset-x-0 h-full bg-gradient-to-t from-surface w-full rounded-b-base" />
      <div className="absolute bottom-0 flex justify-center w-full">
        <div className="flex flex-col">
          <Button
            as={Link}
            to={generateChatPath(chatId)}
            variant="primary"
            className="shrink-0"
            trailingIcon={<SvgIcon name="arrow-right" className="ms-2" />}
          >
            <T
              defaultMessage="Continue the existing conversation"
              id="Messaging.NewMessage.ContinueExistingConversation"
            />
          </Button>
        </div>
      </div>
    </div>
  )
}
